/**
 * FS-501 — device tag text rules and typeahead ranking.
 *
 * `normalizeTagName`, `editDistance` and `findNearMisses` mirror
 * device-data-api's `src/utils/deviceTags.ts`. The copy here drives live UI
 * feedback only: the server normalizes again on write and a UNIQUE index on
 * the stored name is the actual guarantee, so if this copy ever drifts the
 * vocabulary still cannot end up with duplicates.
 *
 * `rankTags` is client-only. The Device List loads the whole tag vocabulary
 * once (it is tens of rows, not thousands), so ranking happens locally and a
 * keystroke costs no request.
 */

export const MAX_TAG_LENGTH = 32;

/** Trim, lowercase, collapse inner whitespace runs to a single space. */
export function normalizeTagName(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

const ALLOWED_RE = /^[a-z0-9 _-]+$/;

/** Null when the name is usable; otherwise the reason to show the operator. */
export function tagNameError(raw: string): string | null {
  const value = normalizeTagName(raw);
  if (value.length === 0) return 'Enter a tag name';
  if (value.length > MAX_TAG_LENGTH) return `Tags are limited to ${MAX_TAG_LENGTH} characters`;
  if (!ALLOWED_RE.test(value)) return 'Use only letters, digits, spaces, hyphens and underscores';
  return null;
}

export function nearMissThreshold(length: number): number {
  return length >= 6 ? 2 : 1;
}

/** Levenshtein distance, abandoned once it provably exceeds `max`. */
export function editDistance(a: string, b: string, max = Infinity): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/** Existing tags close enough that creating `candidate` is probably a typo. */
export function findNearMisses(candidate: string, existing: string[]): string[] {
  const name = normalizeTagName(candidate);
  if (!name) return [];
  const threshold = nearMissThreshold(name.length);
  return existing
    .map((e) => ({ name: e, distance: editDistance(name, e, threshold) }))
    .filter((e) => e.name !== name && e.distance <= threshold)
    .sort((a, b) => a.distance - b.distance || a.name.localeCompare(b.name))
    .map((e) => e.name);
}

/** True when every character of `q` appears in `s` in order (fuzzy match). */
function isSubsequence(q: string, s: string): boolean {
  let i = 0;
  for (let j = 0; j < s.length && i < q.length; j++) {
    if (s[j] === q[i]) i++;
  }
  return i === q.length;
}

/**
 * Match tier, lower is better. The tiers exist so an exact or prefix hit can
 * never be pushed below a loose fuzzy hit — the whole point of the typeahead
 * is that typing `laun` surfaces `laundromat` before the operator can invent
 * `laundry`, so the obvious match has to sit at the top.
 */
function matchTier(q: string, name: string): number | null {
  if (name === q) return 0;
  if (name.startsWith(q)) return 1;
  if (name.includes(q)) return 2;
  if (isSubsequence(q, name)) return 3;
  // Catches transpositions and wrong letters, which no substring test finds.
  if (editDistance(q, name, nearMissThreshold(q.length)) <= nearMissThreshold(q.length)) return 4;
  return null;
}

export interface RankableTag {
  name: string;
  device_count?: number;
}

/**
 * Existing tags matching `query`, best first. An empty query returns
 * everything (the operator opened the box to browse), ordered by how used each
 * tag is so the live vocabulary leads and one-off tags sink.
 */
export function rankTags<T extends RankableTag>(query: string, tags: T[]): T[] {
  const q = normalizeTagName(query);
  if (!q) {
    return [...tags].sort(
      (a, b) => (b.device_count ?? 0) - (a.device_count ?? 0) || a.name.localeCompare(b.name),
    );
  }
  return tags
    .map((t) => ({ tag: t, tier: matchTier(q, t.name) }))
    .filter((m): m is { tag: T; tier: number } => m.tier !== null)
    .sort(
      (a, b) =>
        a.tier - b.tier ||
        (b.tag.device_count ?? 0) - (a.tag.device_count ?? 0) ||
        a.tag.name.localeCompare(b.tag.name),
    )
    .map((m) => m.tag);
}

/** True when the query names an existing tag outright (nothing to create). */
export function isExistingTag(query: string, tags: RankableTag[]): boolean {
  const q = normalizeTagName(query);
  return !!q && tags.some((t) => t.name === q);
}
